import React, { useState, useEffect } from 'react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { SheetView } from '../SheetEditor';
import DocxViewer from './DocxViewer';
import { SPREADSHEET_TYPES, DOCX_TYPES } from './constants';
import styles from './DocumentViewer.module.scss';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const PdfViewer: React.FC<{ fileUrl: string }> = ({ fileUrl }) => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const defaultLayoutPluginInstance = defaultLayoutPlugin();

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);
        setBlobUrl(null);

        const fetchPdf = async () => {
            try {
                const response = await fetch(fileUrl);
                if (!response.ok) {
                    throw new Error(`Failed to fetch PDF: ${response.status}`);
                }
                const blob = await response.blob();
                if (isMounted) {
                    const url = URL.createObjectURL(blob);
                    setBlobUrl(url);
                    setLoading(false);
                }
            } catch (err) {
                console.error('Error fetching PDF:', err);
                if (isMounted) {
                    setError(err instanceof Error ? err.message : 'Failed to load PDF');
                    setLoading(false);
                }
            }
        };

        fetchPdf();

        return () => {
            isMounted = false;
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [fileUrl]);

    if (loading) {
        return (
            <div className={styles.loadingBox}>
                <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
            </div>
        );
    }

    if (error || !blobUrl) {
        return (
            <div className={styles.errorBox}>
                <p className={styles.errorText}>Unable to preview this PDF</p>
                <div className={styles.errorButtonRow}>
                    <button
                        type="button"
                        onClick={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')}
                        className={styles.errorButton}
                    >
                        Open in New Tab
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pdfContainer}>
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                <Viewer
                    fileUrl={blobUrl}
                    plugins={[defaultLayoutPluginInstance]}
                    // Where pdf.js fetches the 14 standard (non-embedded) PDF fonts
                    // (Helvetica, Dingbats, …). Without it, documents referencing one
                    // log "fetchStandardFontData: failed to fetch FoxitXxx.pfb" and
                    // render that font with a substitute. Same CDN+version as the
                    // worker above — keep the two in lockstep.
                    transformGetDocumentParams={(options: any) => ({
                        ...options,
                        standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@3.4.120/standard_fonts/',
                    })}
                    renderError={() => (
                        <div className={styles.errorBoxFull}>
                            <p className={styles.errorText}>Unable to render this PDF</p>
                            <button onClick={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')} className={styles.errorButton}>
                                Open in New Tab
                            </button>
                        </div>
                    )}
                />
            </Worker>
        </div>
    );
};

interface FileViewerProps {
    fileUrl: string;
    mimeType?: string;
}

/** Pure router — no hooks here, each branch renders its own component. */
const DocumentViewer: React.FC<FileViewerProps> = ({ fileUrl, mimeType }) => {
    if (mimeType?.startsWith('image/')) {
        return (
            <div className={styles.imageContainer}>
                <img src={fileUrl} alt="preview" className={styles.previewImage} />
            </div>
        );
    }

    if (mimeType && SPREADSHEET_TYPES.includes(mimeType)) {
        return <SheetView fileUrl={fileUrl} mimeType={mimeType} />;
    }

    if (mimeType && DOCX_TYPES.includes(mimeType)) {
        return <DocxViewer fileUrl={fileUrl} />;
    }

    return <PdfViewer fileUrl={fileUrl} />;
};

export default DocumentViewer;
